import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { ListIcon, LinkIcon } from './Icons';

// Minimal contentEditable rich-text composer — bold/italic/underline/list/
// link, via document.execCommand. Deprecated but still universally
// supported, and by far the simplest zero-dependency way to get this;
// pulling in a full editor library (Quill/TipTap/etc) would be the first
// heavy UI dependency anywhere in this app, which otherwise sticks to raw
// fetch/plain React throughout.
//
// Uncontrolled by design: content lives in the DOM, not React state — a
// contentEditable that round-trips its own innerHTML through React on every
// keystroke fights the browser's cursor/selection handling (the cursor jumps
// to the start on every render). The parent reads content via the ref
// (getHTML/getText) only when it actually needs it (on send), and gets a
// plain-text `onChange` callback purely for things like enabling the Send
// button — that value is never written back into the editor.
const RichTextEditor = forwardRef(function RichTextEditor({ placeholder, disabled, onChange, onKeyDown, ariaLabel }, ref) {
  const editorRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getHTML: () => editorRef.current?.innerHTML || '',
    getText: () => editorRef.current?.innerText || '',
    isEmpty: () => !editorRef.current || editorRef.current.innerText.trim() === '',
    clear: () => {
      if (editorRef.current) editorRef.current.innerHTML = '';
    },
    focus: () => editorRef.current?.focus(),
    // Inserts at the cursor (or at the end, if the editor isn't focused yet):
    // quoted text, a quick reply.
    insertText: (text) => {
      editorRef.current?.focus();
      document.execCommand('insertText', false, text);
      onChange?.(editorRef.current?.innerText || '');
    },
    // Replaces everything (a fresh reply with the agent's signature).
    setHTML: (html) => {
      if (!editorRef.current) return;
      editorRef.current.innerHTML = html || '';
      onChange?.(editorRef.current.innerText || '');
    },
    // Puts the cursor at the very start, above a signature.
    focusStart: () => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      const range = document.createRange();
      range.setStart(editor, 0);
      range.collapse(true);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    },
  }));

  // preventDefault on mousedown (not click) stops the browser from moving
  // focus/selection out of the editor and into the button before
  // execCommand runs — without this, every toolbar button would apply to an
  // empty selection instead of whatever text was actually selected.
  const exec = (command, value) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    onChange?.(editorRef.current?.innerText || '');
  };
  const preventFocusSteal = (event) => event.preventDefault();

  const insertLink = () => {
    const url = window.prompt('Link URL');
    if (url) exec('createLink', url);
  };

  return (
    <div className={`rich-text-editor ${disabled ? 'rich-text-editor-disabled' : ''}`}>
      <div className="rich-text-toolbar">
        <button
          type="button"
          className="icon-btn rich-text-btn"
          onMouseDown={preventFocusSteal}
          onClick={() => exec('bold')}
          disabled={disabled}
          aria-label="Bold"
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className="icon-btn rich-text-btn"
          onMouseDown={preventFocusSteal}
          onClick={() => exec('italic')}
          disabled={disabled}
          aria-label="Italic"
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className="icon-btn rich-text-btn"
          onMouseDown={preventFocusSteal}
          onClick={() => exec('underline')}
          disabled={disabled}
          aria-label="Underline"
        >
          <u>U</u>
        </button>
        <span className="whatsapp-format-divider" aria-hidden="true" />
        <button
          type="button"
          className="icon-btn"
          onMouseDown={preventFocusSteal}
          onClick={() => exec('insertUnorderedList')}
          disabled={disabled}
          aria-label="Bullet list"
        >
          <ListIcon width={15} height={15} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onMouseDown={preventFocusSteal}
          onClick={insertLink}
          disabled={disabled}
          aria-label="Insert link"
        >
          <LinkIcon width={15} height={15} />
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-text-body"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel || placeholder}
        onInput={() => onChange?.(editorRef.current?.innerText || '')}
        onKeyDown={onKeyDown}
      />
    </div>
  );
});

export default RichTextEditor;
